/**
 * The autopilot's A*: an eight-way search over a grid of cells with the
 * mountains rasterised as blocked, an octile heuristic, and a binary heap
 * with decrease-key so each cell sits in the open set once. A search
 * allocates nothing but its result: the scores, parents, closed set and heap
 * are typed arrays made once and refilled per search. A path comes back as
 * packed cells, (y << 16) | x, start to goal inclusive, so a grid may be up
 * to 65536 cells a side. A diagonal move needs both neighbouring cardinals
 * clear, the dontCrossCorners option of the pathfinding.js SWAM's autopilot
 * used, and the line-of-sight test behind the smoothing applies the same
 * rule, so a smoothed path never sweeps a blocked corner. That test walks
 * cells Bresenham-style: it pulls a string of cell centres tight and is no
 * swept check for a plane with a width. The non-null assertions are
 * typed-array reads whose index a loop or a bounds check has already proven.
 */

const STRAIGHT = 1;
const DIAGONAL = Math.SQRT2;
const OCTILE_FACTOR = Math.SQRT2 - 1;

/** The largest coordinate the packing holds on each axis. */
const MAX_COORD = 0xffff;

/** Packs a cell as (y << 16) | x, unsigned. */
function packCoord(x: number, y: number): number {
  return ((y << 16) | x) >>> 0;
}

/** The x of a packed cell. */
export function unpackX(packed: number): number {
  return packed & MAX_COORD;
}

/** The y of a packed cell. */
export function unpackY(packed: number): number {
  return (packed >>> 16) & MAX_COORD;
}

export class Pathfinder {
  private readonly width: number;
  private readonly height: number;
  /** 0 clear, 1 blocked; written freely between searches. */
  readonly blocked: Uint8Array;
  /**
   * A cost added on entering each cell on top of the step, or null for none.
   * Non-negative values keep the search exact; AutoPilot uses it to keep a
   * path a cell off the mountains where there is room.
   */
  enterPenalty: Float32Array | null = null;

  private readonly gScore: Float32Array;
  private readonly parent: Int32Array;
  private readonly closed: Uint8Array;
  /** The open heap as parallel arrays; heapPos holds each cell's slot in it, or -1. */
  private readonly heapCost: Float32Array;
  private readonly heapIndex: Uint32Array;
  private readonly heapPos: Int32Array;
  private heapSize = 0;

  constructor(width: number, height: number) {
    if (width <= 0 || height <= 0 || width > MAX_COORD + 1 || height > MAX_COORD + 1) {
      throw new RangeError(`a grid is 1 to ${MAX_COORD + 1} cells a side, not ${width} by ${height}`);
    }
    const cells = width * height;
    this.width = width;
    this.height = height;
    this.blocked = new Uint8Array(cells);
    this.gScore = new Float32Array(cells);
    this.parent = new Int32Array(cells);
    this.closed = new Uint8Array(cells);
    this.heapCost = new Float32Array(cells);
    this.heapIndex = new Uint32Array(cells);
    this.heapPos = new Int32Array(cells);
  }

  /**
   * The cells from (sx, sy) to (tx, ty), packed, start and goal inclusive;
   * null when either is off the grid or blocked, or no path exists.
   */
  findPath(sx: number, sy: number, tx: number, ty: number): Uint32Array | null {
    const w = this.width;
    const h = this.height;
    if (sx < 0 || sx >= w || sy < 0 || sy >= h || tx < 0 || tx >= w || ty < 0 || ty >= h) {
      return null;
    }

    const blocked = this.blocked;
    const startIdx = sy * w + sx;
    const goalIdx = ty * w + tx;
    if (blocked[startIdx] !== 0 || blocked[goalIdx] !== 0) {
      return null;
    }
    if (startIdx === goalIdx) {
      const out = new Uint32Array(1);
      out[0] = packCoord(sx, sy);
      return out;
    }

    const gScore = this.gScore;
    const closed = this.closed;
    gScore.fill(Infinity);
    this.parent.fill(-1);
    closed.fill(0);
    this.heapPos.fill(-1);
    this.heapSize = 0;

    gScore[startIdx] = 0;
    this.heapInsertOrDecrease(startIdx, this.octile(sx, sy, tx, ty));

    while (this.heapSize > 0) {
      const currentIdx = this.heapPop();
      if (currentIdx === goalIdx) {
        return this.tracePath(startIdx, goalIdx);
      }
      closed[currentIdx] = 1;

      const cx = currentIdx % w;
      const cy = (currentIdx - cx) / w;
      const currentG = gScore[currentIdx]!;

      const upOpen = cy > 0 && blocked[currentIdx - w] === 0;
      const downOpen = cy < h - 1 && blocked[currentIdx + w] === 0;
      const leftOpen = cx > 0 && blocked[currentIdx - 1] === 0;
      const rightOpen = cx < w - 1 && blocked[currentIdx + 1] === 0;

      if (upOpen) this.relax(currentIdx - w, currentIdx, currentG + STRAIGHT, cx, cy - 1, tx, ty);
      if (downOpen) this.relax(currentIdx + w, currentIdx, currentG + STRAIGHT, cx, cy + 1, tx, ty);
      if (leftOpen) this.relax(currentIdx - 1, currentIdx, currentG + STRAIGHT, cx - 1, cy, tx, ty);
      if (rightOpen) this.relax(currentIdx + 1, currentIdx, currentG + STRAIGHT, cx + 1, cy, tx, ty);

      // A diagonal only when both adjacent cardinals are clear: no corner cutting
      if (upOpen && leftOpen && blocked[currentIdx - w - 1] === 0) {
        this.relax(currentIdx - w - 1, currentIdx, currentG + DIAGONAL, cx - 1, cy - 1, tx, ty);
      }
      if (upOpen && rightOpen && blocked[currentIdx - w + 1] === 0) {
        this.relax(currentIdx - w + 1, currentIdx, currentG + DIAGONAL, cx + 1, cy - 1, tx, ty);
      }
      if (downOpen && leftOpen && blocked[currentIdx + w - 1] === 0) {
        this.relax(currentIdx + w - 1, currentIdx, currentG + DIAGONAL, cx - 1, cy + 1, tx, ty);
      }
      if (downOpen && rightOpen && blocked[currentIdx + w + 1] === 0) {
        this.relax(currentIdx + w + 1, currentIdx, currentG + DIAGONAL, cx + 1, cy + 1, tx, ty);
      }
    }

    return null;
  }

  /**
   * Pulls a path tight: every cell whose neighbours see each other straight is
   * dropped, so the plane steers at the corners only. A fresh array; both ends stay.
   */
  smoothPath(path: Uint32Array): Uint32Array {
    if (path.length <= 2) return new Uint32Array(path);
    const keep: number[] = [0];
    let anchor = 0;
    for (let j = 2; j < path.length; j++) {
      const pa = path[anchor]!;
      const pj = path[j]!;
      if (!this.hasLineOfSight(unpackX(pa), unpackY(pa), unpackX(pj), unpackY(pj))) {
        keep.push(j - 1);
        anchor = j - 1;
      }
    }
    keep.push(path.length - 1);
    const out = new Uint32Array(keep.length);
    for (let i = 0; i < keep.length; i++) out[i] = path[keep[i]!]!;
    return out;
  }

  /**
   * Whether the line between two cells crosses clear cells only, walked
   * Bresenham-style; a diagonal step needs both adjacent cardinals clear, as
   * the search does. The cells come from a path, so they are on the grid.
   */
  private hasLineOfSight(x0: number, y0: number, x1: number, y1: number): boolean {
    const w = this.width;
    const blocked = this.blocked;

    let dx = x1 - x0;
    let dy = y1 - y0;
    const sx = dx < 0 ? -1 : 1;
    const sy = dy < 0 ? -1 : 1;
    if (dx < 0) dx = -dx;
    if (dy < 0) dy = -dy;
    let err = dx - dy;
    let x = x0;
    let y = y0;

    for (;;) {
      if (blocked[y * w + x] !== 0) return false;
      if (x === x1 && y === y1) return true;
      const oldX = x;
      const oldY = y;
      const e2 = err * 2;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
      if (x !== oldX && y !== oldY) {
        if (blocked[oldY * w + x] !== 0) return false;
        if (blocked[y * w + oldX] !== 0) return false;
      }
    }
  }

  /** Offers a neighbour the cost of reaching it through the current cell, and queues it if that is an improvement. */
  private relax(
    neighborIdx: number,
    fromIdx: number,
    tentativeG: number,
    nx: number,
    ny: number,
    tx: number,
    ty: number,
  ): void {
    if (this.closed[neighborIdx] === 1) return;
    const penalty = this.enterPenalty;
    const adjustedG = penalty !== null ? tentativeG + penalty[neighborIdx]! : tentativeG;
    if (adjustedG >= this.gScore[neighborIdx]!) return;
    this.gScore[neighborIdx] = adjustedG;
    this.parent[neighborIdx] = fromIdx;
    this.heapInsertOrDecrease(neighborIdx, adjustedG + this.octile(nx, ny, tx, ty));
  }

  /** Walks the parents back from the goal and packs the path start first. */
  private tracePath(startIdx: number, goalIdx: number): Uint32Array {
    const w = this.width;
    const parent = this.parent;
    let length = 1;
    for (let cur = goalIdx; cur !== startIdx; cur = parent[cur]!) {
      length++;
      if (length > parent.length) throw new Error('path trace overflow');
    }
    const out = new Uint32Array(length);
    let i = length - 1;
    for (let cur = goalIdx; ; cur = parent[cur]!) {
      const x = cur % w;
      const y = (cur - x) / w;
      out[i--] = packCoord(x, y);
      if (cur === startIdx) break;
    }
    return out;
  }

  /** The octile distance: diagonal steps where both axes have distance left, straight steps for the rest. */
  private octile(x1: number, y1: number, x2: number, y2: number): number {
    const dx = x1 < x2 ? x2 - x1 : x1 - x2;
    const dy = y1 < y2 ? y2 - y1 : y1 - y2;
    return dx > dy ? dx + OCTILE_FACTOR * dy : dy + OCTILE_FACTOR * dx;
  }

  /** Queues a cell, or lowers the cost of its one entry in the heap. */
  private heapInsertOrDecrease(idx: number, cost: number): void {
    const pos = this.heapPos[idx]!;
    if (pos >= 0) {
      if (cost >= this.heapCost[pos]!) return;
      this.heapCost[pos] = cost;
      this.heapBubbleUp(pos);
      return;
    }
    const i = this.heapSize++;
    this.heapCost[i] = cost;
    this.heapIndex[i] = idx;
    this.heapPos[idx] = i;
    this.heapBubbleUp(i);
  }

  private heapPop(): number {
    const cs = this.heapCost;
    const is = this.heapIndex;
    const hp = this.heapPos;
    const result = is[0]!;
    hp[result] = -1;
    const last = --this.heapSize;
    if (last === 0) return result;
    cs[0] = cs[last]!;
    is[0] = is[last]!;
    hp[is[0]!] = 0;
    this.heapBubbleDown(0);
    return result;
  }

  private heapBubbleUp(start: number): void {
    const cs = this.heapCost;
    const is = this.heapIndex;
    const hp = this.heapPos;
    let i = start;
    while (i > 0) {
      const parent = (i - 1) >>> 1;
      if (cs[parent]! <= cs[i]!) break;
      const tc = cs[i]!;
      const ti = is[i]!;
      cs[i] = cs[parent]!;
      is[i] = is[parent]!;
      hp[is[i]!] = i;
      cs[parent] = tc;
      is[parent] = ti;
      hp[ti] = parent;
      i = parent;
    }
  }

  private heapBubbleDown(start: number): void {
    const cs = this.heapCost;
    const is = this.heapIndex;
    const hp = this.heapPos;
    const last = this.heapSize;
    let i = start;
    for (;;) {
      const l = i * 2 + 1;
      if (l >= last) break;
      const r = l + 1;
      let smallest = i;
      if (cs[l]! < cs[smallest]!) smallest = l;
      if (r < last && cs[r]! < cs[smallest]!) smallest = r;
      if (smallest === i) break;
      const tc = cs[i]!;
      const ti = is[i]!;
      cs[i] = cs[smallest]!;
      is[i] = is[smallest]!;
      hp[is[i]!] = i;
      cs[smallest] = tc;
      is[smallest] = ti;
      hp[ti] = smallest;
      i = smallest;
    }
  }
}
